import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { Grid, Paper } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isActiveVersionAVersion, isShowUnspecified, TitleEntity } from '@/redux/slices/slice_types';
import { setActiveVersion } from '@/redux/slices/titleV2Slice';
import { acceptVersionsFromTitleSlice } from '@/redux/slices/orderPlacementSlice';
import { selectCanViewAvailWindows, selectCanViewInventory } from '@/redux/selectors/authSelectors';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { selectOutputVersionOptions, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { selectActiveTitle, selectActiveVersion, selectActiveTitleVersions } from '@/redux/selectors/titleV2Selectors';

import { useQuery } from '@/components/_hooks/useQuery';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import Tab from '@/components/_common/Tabs/Tab';
import Tabs from '@/components/_common/Tabs/Tabs';
import TabPanel from '@/components/_common/Tabs/TabPanel';
import PolarisIcon from '@/components/_common/PolarisIcon';
import TabHeading from '@/components/_common/Tabs/TabHeading';
import VersionSelect, { UNSPECIFIED_VERSION } from '@/components/_common/VersionSelect';
import TitleDetailInventoryV2 from '@/components/TitleDetail/v2/TitleDetailInventoryV2/TitleDetailInventoryV2';
import TitleCustomerAssignmentV2 from '@/components/TitleDetail/v2/TitleCustomerAssignmentV2';
import TitleDetailMetadataV2 from '@/components/TitleDetail/v2/TitleDetailMetadataV2/TitleDetailMetadataV2';
import TitleDetailMetadataSchemaV2 from '@/components/TitleDetail/v2/TitleDetailMetadataV2/TitleDetailMetadataSchemaV2';
import TitleDetailExtraMetadataV2 from '@/components/TitleDetail/v2/TitleDetailMetadataV2/TitleDetailExtraMetadataV2';
import TitleDetailSynopsisV2 from '@/components/TitleDetail/v2/TitleDetailMetadataV2/TitleDetailSynopsisV2';
import TitleDetailRelatedJobsV2 from '@/components/TitleDetail/v2/TitleDetailRelatedJobsV2/TitleDetailRelatedJobsV2';
import { useTitleURLParser } from '@/components/TitleDetail/v2/hooks/useTitleURLParser';
import { buildTitleURL, BuildTitleURLParams } from '@/_utils/titleUtils';
import TitleDetailAvailsV2 from './TitleDetailAvailsV2/TitleDetailAvailsV2';

const TabNames = ['metadata', 'inventory', 'related_jobs', 'avails'];

const TitleDetailTabsV2 = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const canViewInventory = useAppSelector(selectCanViewInventory);
  const canViewAvails = useAppSelector(selectCanViewAvailWindows);
  const activeTitleVersions = useAppSelector(selectActiveTitleVersions);
  const outputVersionOptions = useAppSelector(selectOutputVersionOptions);
  const versionKeys = useAppSelector(selectVersionKeys);
  const activeVersion = useAppSelector(selectActiveVersion);
  const activeTitle = useAppSelector(selectActiveTitle);
  const dataTableFilters = useAppSelector(selectDataTableFilters);
  const showRelatedJobs = useContext(ConfigContext)?.titleConfig?.shouldShowRelatedJobs;
  const showAvails = useContext(ConfigContext)?.titleConfig?.showAvails && canViewAvails;
  const versionSelectShowUnspecifiedVersion = useConfigValue(['versionSelectShowUnspecifiedVersion']);

  const { path, activeTitleId } = useTitleURLParser();
  const query = useQuery();
  const [tab, setTab] = useState(0);

  const selectedTab = query.get('tab');
  const versionId = query.get('version_id');

  const versions = outputVersionOptions[activeTitleId] || [];
  const hasVersions = (activeTitleVersions?.length ?? 0) > 0;

  const dropdownOptions = versions.map((v: TitleEntity) => v.id);
  // dataTableFilters?.search contains the search query from the search bar on the Titles page (if such a query was made)
  const searchedVersionId = dataTableFilters?.search
    ? versions.find((version) =>
        [
          version.rightsline_id,
          version.phoenix7_id?.toLowerCase(),
          version.external_id?.toLowerCase(),
          version.alt_external_id?.toString()?.toLowerCase()
        ].includes(dataTableFilters.search.toLowerCase())
      )?.id
    : undefined;

  useEffect(() => {
    if (!versionId) {
      dispatch(setActiveVersion(null));
    }
  }, [versionId]);

  const handleTabChange = (_e: any, newTab: number) => {
    setTab(newTab);
    history.push(buildTitleURL({ path, tab: TabNames[newTab], version: versionId ?? undefined }));
  };

  useEffect(() => {
    if (!versions?.length) return;

    if (query.get('version_id') === 'all') {
      dispatch(setActiveVersion({}));
      return;
    }

    const queryVersionId = query.get('version_id');
    const firstVersionId = versions[0].id;

    if (!queryVersionId) {
      selectVersion({ target: { value: searchedVersionId || firstVersionId } });
      return;
    }

    if (queryVersionId === UNSPECIFIED_VERSION) {
      selectVersion({ target: { value: UNSPECIFIED_VERSION } });
    } else if (!versions.find((v) => String(v.id) === queryVersionId)) {
      selectVersion({ target: { value: firstVersionId } });
    }
  }, [versions, searchedVersionId]);

  useEffect(() => {
    if (selectedTab) {
      setTab(TabNames.indexOf(selectedTab));
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!activeTitleId) return;
    dispatch(acceptVersionsFromTitleSlice({ versions: activeTitleVersions ?? [], id: activeTitleId }));
    if (!hasVersions && tab === 3) setTab(0);
  }, [activeTitleId, activeTitleVersions]);

  useEffect(() => {
    if (!versionId || !versionKeys || !versionKeys[versionId]) return;
    dispatch(setActiveVersion(versionKeys[versionId]));
  }, [versionId, versionKeys]);

  const selectVersion = (e: any) => {
    const versionId = e.target.value;
    const params: BuildTitleURLParams = { path, tab: TabNames[tab] };

    if (versionId === UNSPECIFIED_VERSION && versionSelectShowUnspecifiedVersion) {
      dispatch(setActiveVersion({ showUnspecified: true }));
    } else if (!versionId) {
      params.version = 'all';
      dispatch(setActiveVersion({}));
    } else {
      params.version = versionId;
    }
    history.push(buildTitleURL(params));
  };

  return (
    <Grid item sx={{ mt: 5, position: 'relative' }}>
      {/* VERSIONS DROPDOWN */}
      <Grid
        container
        item
        justifyContent="flex-end"
        alignItems="center"
        sx={{ position: 'relative', minHeight: 54, ml: 'auto', maxWidth: 500, mb: -5, zIndex: 999 }}
      >
        {hasVersions && (
          <VersionSelect
            showUnspecifiedVersionOption={versionSelectShowUnspecifiedVersion}
            label={!activeVersion || versionId === 'all' ? 'All' : 'Version'}
            value={isShowUnspecified(activeVersion) ? UNSPECIFIED_VERSION : isActiveVersionAVersion(activeVersion) ? activeVersion.id : null}
            options={dropdownOptions}
            onChange={selectVersion}
            disableClearable={false}
            forceEnableOptions
            sx={{ width: 300 }}
            highContrast
          />
        )}
        <TitleCustomerAssignmentV2 />
      </Grid>

      <Tabs value={tab} onChange={handleTabChange} aria-label="title detail tabs">
        <Tab label="Metadata" />
        {canViewInventory && <Tab label="Inventory" />}
        {showRelatedJobs && <Tab label="Related Jobs" />}
        {showAvails && hasVersions && <Tab label="Avails" />}
      </Tabs>

      <StyledTabPanel value={tab} index={0} style={{ borderTopLeftRadius: 0 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <TabHeading icon={<PolarisIcon icon="info" />}>Metadata</TabHeading>
        </Grid>

        <TitleDetailMetadataV2 />

        {isActiveVersionAVersion(activeVersion) && activeVersion.metadata_schema && <TitleDetailMetadataSchemaV2 />}
        {activeVersion && <TitleDetailExtraMetadataV2 />}

        {activeTitle?.synopsis && <TitleDetailSynopsisV2 />}
      </StyledTabPanel>

      <TabPanel value={tab} index={1}>
        <StyledPaper sx={{ p: 3, pt: 4 }}>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <TabHeading icon={<PolarisIcon icon="files" />}>Inventory</TabHeading>
          </Grid>
          {tab === 1 && canViewInventory && (
            <TitleDetailInventoryV2
              showUnspecified={versionSelectShowUnspecifiedVersion && isShowUnspecified(activeVersion)}
              selectedVersionName={isActiveVersionAVersion(activeVersion) ? activeVersion.name : undefined}
            />
          )}
        </StyledPaper>
      </TabPanel>

      {showRelatedJobs && (
        <StyledTabPanel value={tab} index={2}>
          <Grid container justifyContent="space-between" alignItems="center">
            <TabHeading icon={<PolarisIcon icon="related_jobs" />}>Related Jobs</TabHeading>
          </Grid>
          {tab === 2 && <TitleDetailRelatedJobsV2 lookupId={activeTitleId} isActiveTab={tab === 2} />}
        </StyledTabPanel>
      )}
      {showAvails && hasVersions && (
        <StyledTabPanel value={tab} index={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <TabHeading icon={<PolarisIcon icon="video_outline" />}>Avails</TabHeading>
          </Grid>
          {tab === 3 && <TitleDetailAvailsV2 lookupId={activeTitleId} isActiveTab={tab === 3} />}
        </StyledTabPanel>
      )}
    </Grid>
  );
};

export default TitleDetailTabsV2;

const StyledTabPanel = styled(TabPanel)`
  background: ${(props) => props.theme.palette.background.paper};
  padding: ${(props) => props.theme.spacing(4)};
  border-radius: ${(props) => props.theme.shape.borderRadius};
  box-shadow: 0px 4px 18px rgba(40, 41, 61, 0.05);
`;

const StyledPaper = styled(Paper)`
  border: 0px;
  box-shadow: none !important;
  border-radius: 0px;
`;
